import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS } from '../cardMesh';

// ── Ranged. Medieval bow-and-arrow watermark ────────────────────
//
// Static, non-animated overlay. A drawn longbow with a nocked
// arrow sits atop the card art at low opacity so the underlying
// art still reads through. The SVG path data is the same one used
// by `IconLongbow` in src/components/ui/icons.jsx, so the visual
// language matches the rest of the medieval UI.
//
// One-time canvas bake → shared texture → flat textured plane.
// No frame-by-frame work beyond positioning the plane to follow
// the card mesh.

const RISE_HEIGHT_OFFSET = 0.05;
const PLANE_W = CARD_WIDTH * 0.55;
const PLANE_H = CARD_WIDTH * 0.55;

let _sharedBowTexture = null;

// Pre-render the bow-and-arrow emblem to a 1024² canvas using a
// heraldic engraving recipe:
//   1. Wide dark stroke under everything → cast-shadow / inset edge.
//   2. Slightly inset cream stroke on top → the "ink" of the glyph.
//   3. Thin warm highlight along the upper-left edge → fakes a
//      raised / embossed lighting direction.
// All three passes share the same Path2D objects so the strokes
// register perfectly and the depth illusion holds at any scale.
function getBowTexture() {
  if (_sharedBowTexture) return _sharedBowTexture;

  const size = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Authoring grid: 100×100. The bow's belly bulges right. Toward
  // the target. And the arrow runs horizontally across it, nocked on
  // the drawn bowstring at the left (the archer's side).
  const margin = 60;
  const scale = (size - margin * 2) / 100;
  ctx.translate(margin, margin);
  ctx.scale(scale, scale);

  // ── Recurve longbow body ──
  // Asymmetric curve that bows out right then recurves at the tips
  // (top and bottom flare slightly back toward the string). The
  // belly faces the direction of fire, so the stave and the arrow
  // point the same way. This is the silhouette of an English /
  // heraldic longbow, not the round-D of a cartoon icon.
  const bow = new Path2D();
  bow.moveTo(30, 6);              // top tip
  bow.bezierCurveTo(22, 14, 58, 30, 58, 50);    // upper limb curving right
  bow.bezierCurveTo(58, 70, 22, 86, 30, 94);    // lower limb mirror
  bow.moveTo(30, 6);              // small inward curl at the top tip
  bow.bezierCurveTo(28, 10, 26, 12, 24, 12);
  bow.moveTo(30, 94);             // mirror curl at the bottom tip
  bow.bezierCurveTo(28, 90, 26, 88, 24, 88);

  // ── Bowstring ──
  // Pulled back to the nock point on the left so the V is asymmetric:
  // the arrow is mid-draw, not loose. A real archer's silhouette.
  const string = new Path2D();
  string.moveTo(30, 6);
  string.lineTo(18, 50);
  string.lineTo(30, 94);

  // ── Arrow ──
  // Slim shaft nocked on the string at the left, running right
  // across the bow's grip. A proper leaf-shaped (lozenge) arrowhead
  // instead of an open V, and a single chevron fletching at the nock.
  const shaftAndHead = new Path2D();
  // Shaft
  shaftAndHead.moveTo(18, 50);
  shaftAndHead.lineTo(86, 50);
  // Leaf arrowhead (filled lozenge)
  shaftAndHead.moveTo(86, 50);
  shaftAndHead.lineTo(81, 44);
  shaftAndHead.lineTo(96, 50);
  shaftAndHead.lineTo(81, 56);
  shaftAndHead.closePath();

  const fletching = new Path2D();
  // Chevron fletching at the nock end of the shaft.
  fletching.moveTo(18, 50);
  fletching.lineTo(12, 44);
  fletching.lineTo(16, 50);
  fletching.lineTo(12, 56);
  fletching.closePath();

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // ── PASS 1: dark inset / shadow ──
  ctx.strokeStyle = 'rgba(28, 18, 6, 0.95)';
  ctx.fillStyle = 'rgba(28, 18, 6, 0.95)';
  ctx.lineWidth = 4.2;
  ctx.stroke(bow);
  ctx.lineWidth = 2.4;
  ctx.stroke(string);
  ctx.lineWidth = 3.8;
  ctx.stroke(shaftAndHead);
  ctx.fill(shaftAndHead);
  ctx.fill(fletching);
  ctx.stroke(fletching);

  // ── PASS 2: cream body. The visible "ink" ──
  ctx.strokeStyle = '#efdcb1';
  ctx.fillStyle   = '#efdcb1';
  ctx.lineWidth = 3.0;
  ctx.stroke(bow);
  ctx.lineWidth = 1.5;
  ctx.stroke(string);
  ctx.lineWidth = 2.5;
  ctx.stroke(shaftAndHead);
  ctx.fill(shaftAndHead);
  ctx.fill(fletching);
  ctx.stroke(fletching);

  // ── PASS 3: warm highlight along the upper-left side ──
  // Drawn with the same paths but clipped by composite mode so it
  // only adds to existing pixels. No halo. Slight horizontal
  // offset fakes a lighting direction.
  ctx.globalCompositeOperation = 'source-atop';
  ctx.strokeStyle = 'rgba(255, 238, 196, 0.7)';
  ctx.lineWidth = 1.1;
  ctx.translate(-0.4, -0.4);
  ctx.stroke(bow);
  ctx.lineWidth = 0.8;
  ctx.stroke(shaftAndHead);
  ctx.translate(0.4, 0.4);
  ctx.globalCompositeOperation = 'source-over';

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  _sharedBowTexture = tex;
  return tex;
}

const VERT_SHADER = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// The texture is already pre-shaded (dark inset + cream + highlight
// baked in). Just sample its RGBA, apply a soft circular vignette so
// the watermark fades organically into the art, and scale alpha by
// the configured opacity.
const FRAG_SHADER = /* glsl */ `
  precision highp float;

  uniform sampler2D uIcon;
  uniform float     uOpacity;

  varying vec2 vUv;

  void main() {
    vec4 tex = texture2D(uIcon, vUv);

    vec2 c = vUv - 0.5;
    float vignette = 1.0 - smoothstep(0.42, 0.55, length(c));

    float alpha = tex.a * uOpacity * vignette;
    if (alpha < 0.01) discard;

    gl_FragColor = vec4(tex.rgb, alpha);
  }
`;

export function createRangedBowEffect() {
  const group = new THREE.Group();
  group.frustumCulled = false;

  const geometry = new THREE.PlaneGeometry(PLANE_W, PLANE_H);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uIcon: { value: getBowTexture() },
      uOpacity: { value: 0.78 },
    },
    vertexShader: VERT_SHADER,
    fragmentShader: FRAG_SHADER,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.NormalBlending,
    side: THREE.DoubleSide,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.position.z = CARD_THICKNESS / 2 + RISE_HEIGHT_OFFSET;
  mesh.renderOrder = 17;
  group.add(mesh);

  return { points: group, materials: [material], geometry };
}

const _tmpVec3 = new THREE.Vector3();
const _tmpEuler = new THREE.Euler();
export function updateRangedBowEffect(effect, _dt, cardMesh) {
  if (!effect || !cardMesh) return;

  cardMesh.getWorldPosition(_tmpVec3);
  effect.points.position.copy(_tmpVec3);

  const intent = cardMesh.userData?.intentEuler;
  const yaw = intent ? intent.z : 0;
  _tmpEuler.set(-Math.PI / 2, 0, yaw, 'XYZ');
  effect.points.rotation.copy(_tmpEuler);
}

export function disposeRangedBowEffect(effect) {
  if (!effect) return;
  effect.points.parent?.remove(effect.points);
  effect.geometry?.dispose();
  for (const material of effect.materials || []) {
    material.dispose();
  }
}
